import {
  type EntityCategory,
  type EntityParty,
  type PartyEntityType,
  SortDirection,
  type TaxpayerIdentification,
  type TaxStatus,
  TransactionColumnName,
} from "@digits-graphql/frontend/graphql-bearer"
import numberHelper from "@digits-shared/helpers/numberHelper"
import { molecule, use } from "bunshi/react"
import { atom } from "jotai"
import { atomWithDefault, atomWithReset, RESET } from "jotai/utils"
import { LegalEntityIdScope } from "src/frontend/components/LegalEntityIdScope"
import { newVendorsMolecule } from "src/frontend/components/OS/Applications/VendorDirectory/moleculeHydrators/newVendorsHydrator"
import { VendorDirectoryScope } from "src/frontend/components/OS/Applications/VendorDirectory/VendorScopeProvider"

export enum PartySortFields {
  Vendor = "Vendor Name",
  TotalSpend = "Total Spend",
  TaxQualifiedSpend = "1099 Spend",
  TransactionCount = "Transaction Count",
  EntityType = "Entity Type",
}

const DEFAULT_SORT = new Map([
  [PartySortFields.Vendor, SortDirection.Ascending],
  [PartySortFields.TotalSpend, SortDirection.Descending],
  [PartySortFields.TransactionCount, SortDirection.Descending],
])

// Parties
export const filterPartiesMolecule = molecule(() => {
  use(VendorDirectoryScope)
  use(LegalEntityIdScope)
  // privateSelectedPartiesAtom is the read/write version of selected parties that is not exposed externally
  const privateSelectedAtom = atomWithDefault(() => new Map<string, EntityParty>())

  // selectedPartiesAtom is the read-only version of selected parties that is exposed externally
  const selectedPartiesAtom = atom((get) => get(privateSelectedAtom))
  const toggleSelectedPartyAtom = atom(null, (get, set, party: EntityParty) => {
    const selectedParties = get(privateSelectedAtom)
    selectedParties.has(party.id)
      ? selectedParties.delete(party.id)
      : selectedParties.set(party.id, party)
    set(privateSelectedAtom, new Map(selectedParties))
  })
  const addSelectedPartiesAtom = atom(null, (get, set, parties: EntityParty[]) => {
    const selectedParties = get(privateSelectedAtom)
    parties.forEach((party) => {
      selectedParties.set(party.id, party)
    })

    set(privateSelectedAtom, new Map(selectedParties))
  })
  const removeSelectedPartiesAtom = atom(null, (get, set, parties: EntityParty[]) => {
    const selectedParties = get(privateSelectedAtom)
    parties.forEach((party) => {
      selectedParties.delete(party.id)
    })

    set(privateSelectedAtom, new Map(selectedParties))
  })
  const removeSelectedPartyAtom = atom(null, (get, set, partyId: string) => {
    const selectedParties = get(privateSelectedAtom)
    if (selectedParties.has(partyId)) {
      selectedParties.delete(partyId)
      set(privateSelectedAtom, new Map(selectedParties))
    }
  })
  const selectedPartyCountAtom = atom((get) => get(selectedPartiesAtom).size)
  const resetSelectedPartiesAtom = atom(null, (_, set) => {
    set(privateSelectedAtom, RESET)
  })

  return {
    selectedPartiesAtom,
    toggleSelectedPartyAtom,
    removeSelectedPartyAtom,
    addSelectedPartiesAtom,
    removeSelectedPartiesAtom,
    selectedPartyCountAtom,
    resetSelectedPartiesAtom,
  }
})

// Category
export const filterCategoriesMolecule = molecule(() => {
  use(VendorDirectoryScope)
  use(LegalEntityIdScope)
  // privateSelectedCategoriesMolecule is the read/write version of selected categories that is not exposed externally
  const privateSelectedCategoriesMolecule = atomWithDefault(() => new Map())
  const privateHiddenParentDimensionIdsAtom = atomWithReset(new Set<string>())
  const hiddenParentDimensionIdsAtom = atom((get) => get(privateHiddenParentDimensionIdsAtom))
  const toggleHiddenParentDimensionIdAtom = atom(null, (get, set, dimensionId: string) => {
    const hiddenParentDimensionIds = get(privateHiddenParentDimensionIdsAtom)
    hiddenParentDimensionIds.has(dimensionId)
      ? hiddenParentDimensionIds.delete(dimensionId)
      : hiddenParentDimensionIds.add(dimensionId)
    set(privateHiddenParentDimensionIdsAtom, new Set(hiddenParentDimensionIds))
  })
  const resetHiddenParentDimensionIdsAtom = atom(null, (_, set) => {
    set(privateHiddenParentDimensionIdsAtom, new Set())
  })

  // selectedCategoriesAtom is the read-only version of selected categories that is exposed externally
  const selectedCategoriesAtom = atom((get) => get(privateSelectedCategoriesMolecule))
  const toggleSelectedCategoryAtom = atom(
    null,
    (get, set, category: EntityCategory, childCategories?: EntityCategory[]) => {
      const selectedCategories = get(privateSelectedCategoriesMolecule)
      const isSelected = selectedCategories.has(category.id)
      isSelected
        ? selectedCategories.delete(category.id)
        : selectedCategories.set(category.id, category)

      if (childCategories?.length && childCategories.length > 0) {
        childCategories.forEach((childCategory) => {
          isSelected
            ? selectedCategories.delete(childCategory.id)
            : selectedCategories.set(childCategory.id, childCategory)
        })
      }

      set(privateSelectedCategoriesMolecule, new Map(selectedCategories))
    }
  )
  const removeSelectedCategoryAtom = atom(null, (get, set, categoryId: string) => {
    const selectedCategories = get(privateSelectedCategoriesMolecule)
    if (selectedCategories.has(categoryId)) {
      selectedCategories.delete(categoryId)
      set(privateSelectedCategoriesMolecule, new Map(selectedCategories))
    }
  })
  const selectedCategoryCountAtom = atom((get) => get(selectedCategoriesAtom).size)
  const resetSelectedCategoriesAtom = atom(null, (_, set) => {
    set(privateSelectedCategoriesMolecule, RESET)
  })

  return {
    selectedCategoriesAtom,
    toggleSelectedCategoryAtom,
    removeSelectedCategoryAtom,
    selectedCategoryCountAtom,
    resetSelectedCategoriesAtom,
    hiddenParentDimensionIdsAtom,
    toggleHiddenParentDimensionIdAtom,
    resetHiddenParentDimensionIdsAtom,
  }
})

// MinMax Amount
export const filterMinMaxValuesMolecule = molecule(() => {
  use(VendorDirectoryScope)
  use(LegalEntityIdScope)

  // privateMinValueAtom is the read/write version of the min value that is not exposed externally
  const privateMinValueAtom = atomWithDefault<undefined | number>(() => undefined)
  const minValueAtom = atom((get) => get(privateMinValueAtom))
  const minValueMVAtom = atom((get) =>
    get(minValueAtom) !== undefined
      ? numberHelper.buildMonetaryAmountFromUnmultiplied(get(minValueAtom))
      : undefined
  )
  const setMinValueAtom = atom(null, (get, set, value: number | undefined) => {
    set(privateMinValueAtom, value)
  })

  // privateMaxValueAtom is the read/write version of the max value that is not exposed externally
  const privateMaxValueAtom = atomWithDefault<undefined | number>(() => undefined)
  const maxValueAtom = atom((get) => get(privateMaxValueAtom))
  const maxValueMVAtom = atom((get) =>
    get(maxValueAtom) !== undefined
      ? numberHelper.buildMonetaryAmountFromUnmultiplied(get(maxValueAtom))
      : undefined
  )
  const setMaxValueAtom = atom(null, (get, set, value: number | undefined) => {
    set(privateMaxValueAtom, value)
  })
  const amountCountAtom = atom((get) => (get(minValueAtom) ? 1 : 0) + (get(maxValueAtom) ? 1 : 0))
  const resetMinMaxAtom = atom(null, (_, set) => {
    set(privateMinValueAtom, RESET)
    set(privateMaxValueAtom, RESET)
  })

  return {
    minValueAtom,
    setMinValueAtom,
    minValueMVAtom,
    maxValueAtom,
    setMaxValueAtom,
    maxValueMVAtom,
    amountCountAtom,
    resetMinMaxAtom,
  }
})

export const filterVendorSpendMinMaxValuesMolecule = molecule(() => {
  use(VendorDirectoryScope)
  use(LegalEntityIdScope)

  // privateMinValueAtom is the read/write version of the min value that is not exposed externally
  const privateMinValueAtom = atomWithDefault<undefined | number>(() => undefined)
  const minValueAtom = atom((get) => get(privateMinValueAtom))
  const minValueMVAtom = atom((get) =>
    get(minValueAtom) !== undefined
      ? numberHelper.buildMonetaryAmountFromUnmultiplied(get(minValueAtom))
      : undefined
  )
  const setMinValueAtom = atom(null, (get, set, value: number | undefined) => {
    set(privateMinValueAtom, value)
  })

  // privateMaxValueAtom is the read/write version of the max value that is not exposed externally
  const privateMaxValueAtom = atomWithDefault<undefined | number>(() => undefined)
  const maxValueAtom = atom((get) => get(privateMaxValueAtom))
  const maxValueMVAtom = atom((get) =>
    get(maxValueAtom) !== undefined
      ? numberHelper.buildMonetaryAmountFromUnmultiplied(get(maxValueAtom))
      : undefined
  )
  const setMaxValueAtom = atom(null, (get, set, value: number | undefined) => {
    set(privateMaxValueAtom, value)
  })
  const spendFilterCountAtom = atom(
    (get) => (get(minValueAtom) ? 1 : 0) + (get(maxValueAtom) ? 1 : 0)
  )
  const resetSpendAtom = atom(null, (_, set) => {
    set(privateMinValueAtom, RESET)
    set(privateMaxValueAtom, RESET)
  })

  return {
    minValueAtom,
    setMinValueAtom,
    minValueMVAtom,
    maxValueAtom,
    setMaxValueAtom,
    maxValueMVAtom,
    spendFilterCountAtom,
    resetSpendAtom,
  }
})

export const filterTaxQualifiedSpendMinMaxValuesMolecule = molecule(() => {
  use(VendorDirectoryScope)
  use(LegalEntityIdScope)

  // privateMinValueAtom is the read/write version of the min value that is not exposed externally
  const privateMinValueAtom = atomWithDefault<undefined | number>(() => undefined)
  const minValueAtom = atom((get) => get(privateMinValueAtom))
  const minValueMVAtom = atom((get) =>
    get(minValueAtom) !== undefined
      ? numberHelper.buildMonetaryAmountFromUnmultiplied(get(minValueAtom))
      : undefined
  )
  const setMinValueAtom = atom(null, (get, set, value: number | undefined) => {
    set(privateMinValueAtom, value)
  })

  // privateMaxValueAtom is the read/write version of the max value that is not exposed externally
  const privateMaxValueAtom = atomWithDefault<undefined | number>(() => undefined)
  const maxValueAtom = atom((get) => get(privateMaxValueAtom))
  const maxValueMVAtom = atom((get) =>
    get(maxValueAtom) !== undefined
      ? numberHelper.buildMonetaryAmountFromUnmultiplied(get(maxValueAtom))
      : undefined
  )
  const setMaxValueAtom = atom(null, (get, set, value: number | undefined) => {
    set(privateMaxValueAtom, value)
  })
  const spendFilterCountAtom = atom(
    (get) => (get(minValueAtom) !== undefined ? 1 : 0) + (get(maxValueAtom) !== undefined ? 1 : 0)
  )
  const resetSpendAtom = atom(null, (_, set) => {
    set(privateMinValueAtom, RESET)
    set(privateMaxValueAtom, RESET)
  })

  return {
    minValueAtom,
    setMinValueAtom,
    minValueMVAtom,
    maxValueAtom,
    setMaxValueAtom,
    maxValueMVAtom,
    spendFilterCountAtom,
    resetSpendAtom,
  }
})

export const filterMultipleCategoriesMolecule = molecule(() => {
  use(VendorDirectoryScope)
  use(LegalEntityIdScope)

  const checkedAtom = atom(false)
  const toggleAtom = atom(null, (get, set) => {
    set(checkedAtom, !get(checkedAtom))
  })
  const resetAtom = atom(null, (_, set) => {
    set(checkedAtom, false)
  })
  const multipleCategoriesFilterCount = atom((get) => (get(checkedAtom) ? 1 : 0))

  return {
    checkedAtom,
    toggleAtom,
    resetAtom,
    multipleCategoriesFilterCount,
  }
})

export const filterNewVendorsMolecule = molecule(() => {
  use(VendorDirectoryScope)
  use(LegalEntityIdScope)
  const { newPartyIdsAtom, newPartiesAtom } = use(newVendorsMolecule)
  const { selectedPartiesAtom, addSelectedPartiesAtom, removeSelectedPartiesAtom } =
    use(filterPartiesMolecule)

  const checkedAtom = atom((get) => {
    const newPartyIds = get(newPartyIdsAtom)
    const selectedParties = get(selectedPartiesAtom)
    return (
      newPartyIds.length > 0 &&
      newPartyIds.every((partyId) => selectedParties.has(partyId)) &&
      selectedParties.size === newPartyIds.length
    )
  })
  const toggleAtom = atom(null, (get, set) => {
    if (get(checkedAtom)) {
      set(removeSelectedPartiesAtom, Array.from(get(newPartiesAtom)))
    } else {
      set(addSelectedPartiesAtom, Array.from(get(newPartiesAtom)))
    }
  })
  const resetAtom = atom(null, (get, set) => {
    set(removeSelectedPartiesAtom, Array.from(get(newPartiesAtom)))
  })
  const newVendorsFilterCount = atom((get) => (get(checkedAtom) ? 1 : 0))

  return {
    checkedAtom,
    toggleAtom,
    resetAtom,
    newVendorsFilterCount,
  }
})

export const filterW9StatusMolecule = molecule(() => {
  use(VendorDirectoryScope)
  use(LegalEntityIdScope)

  const selectedW9StatusAtom = atom<Set<TaxpayerIdentification> | undefined>(undefined)

  const toggleAtom = atom(null, (get, set, status: TaxpayerIdentification) => {
    get(selectedW9StatusAtom)?.has(status)
      ? set(removeStatusAtom, status)
      : set(addStatusAtom, status)
  })

  const addStatusAtom = atom(null, (get, set, status: TaxpayerIdentification) => {
    set(selectedW9StatusAtom, new Set((get(selectedW9StatusAtom) ?? new Set()).add(status)))
  })

  const removeStatusAtom = atom(null, (get, set, status: TaxpayerIdentification) => {
    const selectedStatus = get(selectedW9StatusAtom)
    if (!selectedStatus) {
      return
    }
    selectedStatus.delete(status)
    selectedStatus.size > 0
      ? set(selectedW9StatusAtom, new Set(selectedStatus))
      : set(selectedW9StatusAtom, undefined)
  })

  const resetAtom = atom(null, (_, set) => {
    set(selectedW9StatusAtom, undefined)
  })
  const countAtom = atom((get) => get(selectedW9StatusAtom)?.size || 0)

  return {
    selectedW9StatusAtom,
    addStatusAtom,
    removeStatusAtom,
    toggleAtom,
    resetAtom,
    countAtom,
  }
})

// Filter 1099 Qualified Vendors
export const filterQualifiedStatusMolecule = molecule(() => {
  use(VendorDirectoryScope)
  use(LegalEntityIdScope)

  const selectedQualifiedStatusAtom = atom<Set<TaxStatus> | undefined>(undefined)

  const toggleQualificationAtom = atom(null, (get, set, status: TaxStatus) => {
    get(selectedQualifiedStatusAtom)?.has(status)
      ? set(removeQualificationStatusAtom, status)
      : set(addQualificationStatusAtom, status)
  })

  const addQualificationStatusAtom = atom(null, (get, set, status: TaxStatus) => {
    set(
      selectedQualifiedStatusAtom,
      new Set((get(selectedQualifiedStatusAtom) ?? new Set()).add(status))
    )
  })

  const removeQualificationStatusAtom = atom(null, (get, set, status: TaxStatus) => {
    const selectedStatus = get(selectedQualifiedStatusAtom)
    if (!selectedStatus) {
      return
    }
    selectedStatus.delete(status)
    selectedStatus.size > 0
      ? set(selectedQualifiedStatusAtom, new Set(selectedStatus))
      : set(selectedQualifiedStatusAtom, undefined)
  })

  const resetQualificationAtom = atom(null, (_, set) => {
    set(selectedQualifiedStatusAtom, undefined)
  })
  const countQualificationAtom = atom((get) => get(selectedQualifiedStatusAtom)?.size || 0)

  return {
    selectedQualifiedStatusAtom,
    addQualificationStatusAtom,
    removeQualificationStatusAtom,
    toggleQualificationAtom,
    resetQualificationAtom,
    countQualificationAtom,
  }
})

// Filter Entity Type Molecule
export const filterEntityTypeMolecule = molecule(() => {
  use(VendorDirectoryScope)
  use(LegalEntityIdScope)

  const selectedEntityTypeAtom = atom<Set<PartyEntityType> | undefined>(undefined)

  const toggleAtom = atom(null, (get, set, type: PartyEntityType) => {
    get(selectedEntityTypeAtom)?.has(type) ? set(removeTypeAtom, type) : set(addTypeAtom, type)
  })

  const addTypeAtom = atom(null, (get, set, type: PartyEntityType) => {
    set(selectedEntityTypeAtom, new Set((get(selectedEntityTypeAtom) ?? new Set()).add(type)))
  })

  const removeTypeAtom = atom(null, (get, set, type: PartyEntityType) => {
    const selectedType: undefined | Set<PartyEntityType> = get(selectedEntityTypeAtom)
    if (!selectedType) {
      return
    }
    selectedType.delete(type)
    selectedType.size > 0
      ? set(selectedEntityTypeAtom, new Set(selectedType))
      : set(selectedEntityTypeAtom, undefined)
  })

  const resetAtom = atom(null, (_, set) => {
    set(selectedEntityTypeAtom, undefined)
  })
  const countAtom = atom((get) => get(selectedEntityTypeAtom)?.size || 0)

  return {
    selectedEntityTypeAtom,
    addTypeAtom,
    removeTypeAtom,
    toggleAtom,
    resetAtom,
    countAtom,
  }
})

export const filterTaxQualificationMolecule = molecule(() => {
  use(VendorDirectoryScope)
  use(LegalEntityIdScope)

  const selectedTaxStatusesAtom = atom<Set<TaxStatus> | undefined>(undefined)

  const toggleAtom = atom(null, (get, set, status: TaxStatus) => {
    get(selectedTaxStatusesAtom)?.has(status)
      ? set(removeStatusAtom, status)
      : set(addStatusAtom, status)
  })

  const addStatusAtom = atom(null, (get, set, status: TaxStatus) => {
    set(selectedTaxStatusesAtom, new Set((get(selectedTaxStatusesAtom) ?? new Set()).add(status)))
  })

  const removeStatusAtom = atom(null, (get, set, status: TaxStatus) => {
    const selectedStatus = get(selectedTaxStatusesAtom)
    if (!selectedStatus) {
      return
    }
    selectedStatus.delete(status)
    selectedStatus.size > 0
      ? set(selectedTaxStatusesAtom, new Set(selectedStatus))
      : set(selectedTaxStatusesAtom, undefined)
  })

  const resetAtom = atom(null, (_, set) => {
    set(selectedTaxStatusesAtom, undefined)
  })
  const countAtom = atom((get) => get(selectedTaxStatusesAtom)?.size || 0)

  return {
    selectedTaxStatusesAtom,
    addStatusAtom,
    removeStatusAtom,
    toggleAtom,
    resetAtom,
    countAtom,
  }
})

// Sort
export const transactionSortMolecule = molecule(() => {
  use(VendorDirectoryScope)
  use(LegalEntityIdScope)
  // privateSortDirectionAtom is the read/write version of the sort direction that is not exposed externally
  const privateSortDirectionAtom = atomWithDefault<SortDirection>(() => SortDirection.Descending)
  const sortDirectionAtom = atom((get) => get(privateSortDirectionAtom))
  // privateSortColumnNameAtom is the read/write version of the sort column name that is not exposed externally
  const privateSortColumnNameAtom = atomWithDefault<TransactionColumnName>(
    () => TransactionColumnName.OccurredAt
  )
  const sortColumnNameAtom = atom((get) => get(privateSortColumnNameAtom))
  const updateSortAtom = atom(null, (get, set, { sortDirection, sortColumnName }) => {
    set(privateSortDirectionAtom, sortDirection)
    set(privateSortColumnNameAtom, sortColumnName)
  })
  const sortCountAtom = atom((get) =>
    get(privateSortDirectionAtom) === SortDirection.Descending &&
    get(privateSortColumnNameAtom) === TransactionColumnName.OccurredAt
      ? 0
      : 1
  )
  const resetSortAtom = atom(null, (_, set) => {
    set(privateSortDirectionAtom, RESET)
    set(privateSortColumnNameAtom, RESET)
  })

  return {
    sortDirectionAtom,
    sortColumnNameAtom,
    updateSortAtom,
    sortCountAtom,
    resetSortAtom,
  }
})

export const vendorSortMolecule = molecule(() => {
  use(VendorDirectoryScope)
  use(LegalEntityIdScope)

  const privateSortAtom = atomWithDefault(() => ({
    field: PartySortFields.Vendor,
    direction: DEFAULT_SORT.get(PartySortFields.Vendor) ?? SortDirection.Descending,
  }))

  const sortAtom = atom((get) => get(privateSortAtom))
  const sortDirectionAtom = atom((get) => get(privateSortAtom).direction)
  const sortFieldAtom = atom((get) => get(privateSortAtom).field)

  const setSortAtom = atom(null, (get, set, field: PartySortFields) => {
    const currentSort = get(privateSortAtom)
    if (currentSort.field === field) {
      set(privateSortAtom, {
        field,
        direction:
          currentSort.direction === SortDirection.Ascending
            ? SortDirection.Descending
            : SortDirection.Ascending,
      })
    } else {
      set(privateSortAtom, {
        field,
        direction: DEFAULT_SORT.get(field) ?? SortDirection.Descending,
      })
    }
  })

  const sortCountAtom = atom((get) =>
    get(privateSortAtom).direction === DEFAULT_SORT.get(PartySortFields.Vendor) &&
    get(privateSortAtom).field === PartySortFields.Vendor
      ? 0
      : 1
  )

  const resetSortAtom = atom(null, (get, set) => {
    set(privateSortAtom, RESET)
  })

  return {
    sortAtom,
    setSortAtom,
    resetSortAtom,
    sortCountAtom,
    sortDirectionAtom,
    sortFieldAtom,
  }
})

export const filterTermMolecule = molecule(() => {
  use(VendorDirectoryScope)
  use(LegalEntityIdScope)

  const filterTermAtom = atomWithDefault<string>(() => "")

  const filterTermCountAtom = atom((get) => (get(filterTermAtom).trim().length > 0 ? 1 : 0))

  const resetFilterTermAtom = atom(null, (_, set) => {
    set(filterTermAtom, "")
  })

  return {
    filterTermAtom,
    filterTermCountAtom,
    resetFilterTermAtom,
  }
})

// All
export const filterGlobalMolecule = molecule(() => {
  use(VendorDirectoryScope)
  use(LegalEntityIdScope)
  const { resetSelectedPartiesAtom, selectedPartyCountAtom } = use(filterPartiesMolecule)
  const { resetSelectedCategoriesAtom, selectedCategoryCountAtom } = use(filterCategoriesMolecule)
  const { resetMinMaxAtom, amountCountAtom } = use(filterMinMaxValuesMolecule)
  const { resetSpendAtom, spendFilterCountAtom } = use(filterVendorSpendMinMaxValuesMolecule)
  const { resetFilterTermAtom, filterTermCountAtom } = use(filterTermMolecule)
  const {
    resetSpendAtom: resetTaxQualifiedSpendAtom,
    spendFilterCountAtom: taxQualifiedSpendFilterCountAtom,
  } = use(filterTaxQualifiedSpendMinMaxValuesMolecule)
  const { resetAtom: resetMultipleCategoriesAtom, multipleCategoriesFilterCount } = use(
    filterMultipleCategoriesMolecule
  )
  const { resetAtom: resetNewVendors, newVendorsFilterCount } = use(filterNewVendorsMolecule)
  const { resetSortAtom, sortCountAtom } = use(transactionSortMolecule)
  const { resetSortAtom: resetVendorSortAtom, sortCountAtom: vendorSortCountAtom } =
    use(vendorSortMolecule)

  const { resetAtom: resetStatusAtom, countAtom: statusCountAtom } = use(filterW9StatusMolecule)
  const { resetQualificationAtom, countQualificationAtom } = use(filterQualifiedStatusMolecule)
  const { resetAtom: resetEntityTypeAtom, countAtom: entityTypeCountAtom } =
    use(filterEntityTypeMolecule)
  const { resetAtom: resetTaxQualificationAtom, countAtom: taxQualificationCountAtom } = use(
    filterTaxQualificationMolecule
  )

  const clearFiltersAtom = atom(null, (get, set) => {
    set(resetSelectedPartiesAtom)
    set(resetSelectedCategoriesAtom)
    set(resetMinMaxAtom)
    set(resetSpendAtom)
    set(resetTaxQualifiedSpendAtom)
    set(resetMultipleCategoriesAtom)
    set(resetNewVendors)
    set(resetStatusAtom)
    set(resetEntityTypeAtom)
    set(resetTaxQualificationAtom)
    set(resetQualificationAtom)

    set(resetSortAtom)
    set(resetVendorSortAtom)
    set(resetFilterTermAtom)
  })

  const totalFilterCountAtom = atom(
    (get) =>
      get(selectedPartyCountAtom) +
      get(selectedCategoryCountAtom) +
      get(amountCountAtom) +
      get(spendFilterCountAtom) +
      get(taxQualifiedSpendFilterCountAtom) +
      get(multipleCategoriesFilterCount) +
      get(newVendorsFilterCount) +
      get(statusCountAtom) +
      get(entityTypeCountAtom) +
      get(taxQualificationCountAtom) +
      get(sortCountAtom) +
      get(vendorSortCountAtom) +
      get(countQualificationAtom) +
      get(filterTermCountAtom)
  )

  return {
    clearFiltersAtom,
    totalFilterCountAtom,
  }
})
